import React, { useMemo, useState } from "react";
import { DndContext, useDraggable, useDroppable, PointerSensor, TouchSensor, useSensor, useSensors, DragOverlay, closestCenter } from "@dnd-kit/core";
import { kgf, getISOWeek, getYear, toNum } from "@/lib/excelCalc";
import { isoWeekMonday } from "@/lib/excelPrint";
import { GripVertical } from "lucide-react";

const DAY_LABELS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const STATUS_CLS = { gestart: "border-l-amber-500", gereed: "border-l-emerald-500", afgegeven: "border-l-blue-500" };

function weekDates(year, week) {
  const mon = isoWeekMonday(toNum(year), toNum(week));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mon);
    d.setUTCDate(mon.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
}

function PlanCard({ p, canEdit, planBadge }) {
  return (
    <div className={`rounded-md border border-slate-200 border-l-4 ${STATUS_CLS[p.status] || "border-l-slate-300"} bg-white px-2 py-1.5 shadow-sm ${p.status === "gereed" ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-1">
        {canEdit && <GripVertical className="h-4 w-4 shrink-0 text-slate-300 mt-0.5" />}
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold text-slate-800" title={p.product}>{p.product || "—"}</div>
          <div className="mt-0.5 flex items-center justify-between gap-1">
            <span className="text-[11px] font-medium text-slate-500">{toNum(p.hoeveelheid) ? `${kgf(p.hoeveelheid).replace(" kg", "")} ${p.eenheid || "kg"}` : "—"}</span>
            {planBadge(p.status)}
          </div>
          {p.opmerking ? <div className="mt-0.5 truncate text-[10px] text-slate-400" title={p.opmerking}>{p.opmerking}</div> : null}
          {p.status === "gereed" && !/gaarlijn/i.test(String(p.lijn || "")) ? <div className="mt-1"><span className="inline-block rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700">→ Klaar voor gaarlijn</span></div> : null}
        </div>
      </div>
    </div>
  );
}

function DraggableCard({ p, canEdit, planBadge }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: String(p.id), data: { p }, disabled: !canEdit });
  return (
    <div ref={setNodeRef} {...(canEdit ? { ...listeners, ...attributes } : {})}
      data-testid="board-card"
      className={`${canEdit ? "cursor-grab active:cursor-grabbing touch-none" : ""} ${isDragging ? "opacity-30" : ""}`}>
      <PlanCard p={p} canEdit={canEdit} planBadge={planBadge} />
    </div>
  );
}

function DayCell({ lijn, datum, items, canEdit, planBadge, isToday }) {
  const { setNodeRef, isOver } = useDroppable({ id: `${lijn}||${datum}`, data: { lijn, datum } });
  return (
    <div ref={setNodeRef} data-testid="board-cell"
      className={`min-h-[70px] rounded-md border p-1 space-y-1 transition-colors ${isOver ? "border-[#1C64B0] bg-blue-50" : isToday ? "border-blue-200 bg-blue-50/40" : "border-slate-200 bg-slate-50/60"}`}>
      {items.map((p) => <DraggableCard key={p.id} p={p} canEdit={canEdit} planBadge={planBadge} />)}
    </div>
  );
}

export default function PlanningBoard({ planning, lines, planYear, planWeek, hideGereed, canEdit, onMove, planBadge }) {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } })
  );
  const dates = useMemo(() => weekDates(planYear, planWeek), [planYear, planWeek]);
  const today = new Date().toISOString().slice(0, 10);

  const weekItems = useMemo(() => (planning || []).filter((p) => p && (!hideGereed || p.status !== "gereed") && getYear(p.datum) === toNum(planYear) && getISOWeek(p.datum) === toNum(planWeek)), [planning, planYear, planWeek, hideGereed]);

  const activePlan = activeId ? weekItems.find((p) => String(p.id) === String(activeId)) : null;

  function handleDragEnd(ev) {
    setActiveId(null);
    const over = ev.over;
    if (!over) return;
    const { lijn, datum } = over.data.current || {};
    const p = weekItems.find((x) => String(x.id) === String(ev.active.id));
    if (!p || !datum) return;
    if (p.datum === datum && p.lijn === lijn) return;
    onMove(p.id, datum, lijn);
  }

  const cellItems = (lijn, datum) => weekItems.filter((p) => p.lijn === lijn && p.datum === datum)
    .sort((a, b) => (a.status === "gereed" ? 1 : 0) - (b.status === "gereed" ? 1 : 0));

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={handleDragEnd} onDragCancel={() => setActiveId(null)}>
      <div className="overflow-x-auto pb-2" data-testid="planning-board">
        <div className="min-w-[900px] space-y-5">
          {lines.map((l) => (
            <div key={l.id} data-testid="board-line">
              <h3 className="mb-2 text-lg font-semibold text-slate-800">{l.naam}</h3>
              <div className="grid grid-cols-7 gap-2">
                {dates.map((d, i) => (
                  <div key={d}>
                    <div className={`mb-1 text-center text-[11px] font-semibold ${d === today ? "text-[#1C64B0]" : "text-slate-400"}`}>
                      {DAY_LABELS[i]} <span className="font-normal">{d.slice(8, 10)}-{d.slice(5, 7)}</span>
                    </div>
                    <DayCell lijn={l.naam} datum={d} items={cellItems(l.naam, d)} canEdit={canEdit} planBadge={planBadge} isToday={d === today} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <DragOverlay>{activePlan ? <div className="w-48"><PlanCard p={activePlan} canEdit={canEdit} planBadge={planBadge} /></div> : null}</DragOverlay>
    </DndContext>
  );
}
